import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Checkbox,
  CheckboxIndicator,
  HeadingText,
  Image,
  StackLayout,
  TextAction,
  useMediaQuery,
  queryHelpers,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { useDocumentTitle } from '@leagueplatform/web-common';
import consentScreenIconSvg from 'assets/consent-screen-icon.svg';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { ConnectionErrorPage } from 'pages/errors/connection-error/connection-error.page';
import { useAcceptUserTerms } from 'hooks/use-accept-user-terms.hook';
import { useSetPostFtux } from 'hooks/use-set-post-ftux.hook';
import { useUpdateEmailNotification } from 'hooks/use-update-email-notification.hook';
import { OnboardingPageWrapper } from 'components/onboarding-page-wrapper/onboarding-page-wrapper.component';

// TODO: Replace with actual URLs when links are provided
const PRIVACY_POLICY_URL = 'https://www.thp.ca/Pages/Privacy.aspx';
const TERMS_URL = 'https://www.thp.ca/Pages/Legaldisclaimer.aspx';

export const ConsentPage = ({
  onComplete,
}: { onComplete?: () => void } = {}) => {
  const { formatMessage } = useIntl();
  const isMobile = useMediaQuery(queryHelpers.down('tablet'));
  const navigate = useNavigate();

  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [optInEmails, setOptInEmails] = useState(false);
  const [showConsentError, setShowConsentError] = useState(false);
  const [showRetryError, setShowRetryError] = useState(false);

  const { mutateAsync: acceptUserTerms, isPending: isTermsPending } =
    useAcceptUserTerms();
  const { mutateAsync: setPostFtux, isPending: isFtuxPending } =
    useSetPostFtux();
  const {
    mutateAsync: updateEmailNotification,
    isPending: isNotificationPending,
  } = useUpdateEmailNotification();
  const isLoading = isTermsPending || isFtuxPending || isNotificationPending;

  useDocumentTitle(formatMessage({ id: 'STR_COMPLETE_YOUR_ACCOUNT' }));

  const onContinue = async () => {
    if (!agreedToTerms) {
      setShowConsentError(true);
      return;
    }

    setShowRetryError(false);

    try {
      await updateEmailNotification({
        opted_into_marketing_communications: optInEmails,
      });
    } catch {
      // Marketing preference failure is non-blocking; proceed anyway.
    }

    try {
      // TODO: replace "1.0" with actual version once THP provides one
      await acceptUserTerms('1.0');
      if (onComplete) {
        onComplete();
      } else {
        await setPostFtux();
        navigate(APP_MANAGED_ROUTES.HOME);
      }
    } catch {
      setShowRetryError(true);
    }
  };

  const emailUpdatesLabel = formatMessage(
    { id: 'STR_CONSENT_EMAIL_UPDATES' },
    { appName: formatMessage({ id: 'STR_APP_NAME' }) },
  );

  if (showRetryError) {
    return <ConnectionErrorPage retry={onContinue} />;
  }

  return (
    <OnboardingPageWrapper
      onButtonClick={onContinue}
      isButtonDisabled={isLoading}
    >
      <StackLayout
        horizontalAlignment="center"
        css={{
          padding: isMobile ? '$five $oneAndHalf $five' : '$twoAndHalf',
          gap: '$oneAndHalf',
        }}
      >
        <HeadingText as="h1" level="1" size="xl">
          {formatMessage({ id: 'STR_COMPLETE_YOUR_ACCOUNT' })}
        </HeadingText>
        <Image src={consentScreenIconSvg} alt="" width="163" height="163" />
        <Box
          css={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '$one',
            paddingInline: '$one',
            marginTop: '$one',
          }}
        >
          <Box>
            <Box
              css={{
                display: 'flex',
                gap: '$half',
                alignItems: 'flex-start',
              }}
            >
              <CheckboxIndicator
                id="consent-privacy-terms"
                name="consent-privacy-terms"
                checked={agreedToTerms}
                onChange={(e) => {
                  setAgreedToTerms(e.target.checked);
                  if (e.target.checked) setShowConsentError(false);
                }}
                aria-labelledby="consent-privacy-terms-label"
                aria-describedby={
                  showConsentError ? 'consent-privacy-terms-error' : undefined
                }
                inputStatus={showConsentError ? 'error' : undefined}
                css={{
                  borderRadius: '0',
                  flexShrink: 0,
                  borderColor: showConsentError
                    ? '$inputBorderCritical'
                    : '$inputBorderDefault',
                }}
              />
              <UtilityText id="consent-privacy-terms-label">
                {`${formatMessage({
                  id: 'STR_CONSENT_HAVE_READ_AND_AGREE',
                })} `}
                <TextAction
                  href={PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${formatMessage({
                    id: 'STR_PRIVACY_POLICY',
                  })} ${formatMessage({ id: 'STR_OPENS_IN_NEW_TAB' })}`}
                  css={{ textDecoration: 'underline', fontWeight: 'inherit' }}
                >
                  {formatMessage({ id: 'STR_PRIVACY_POLICY' })}
                </TextAction>
                {` ${formatMessage({ id: 'STR_AND' })} `}
                <TextAction
                  href={TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${formatMessage({
                    id: 'STR_TERMS_OF_SERVICE',
                  })} ${formatMessage({ id: 'STR_OPENS_IN_NEW_TAB' })}`}
                  css={{ textDecoration: 'underline', fontWeight: 'inherit' }}
                >
                  {formatMessage({ id: 'STR_TERMS_OF_SERVICE' })}
                </TextAction>
                .
              </UtilityText>
            </Box>
            {showConsentError && (
              <Box
                as="p"
                role="alert"
                id="consent-privacy-terms-error"
                css={{
                  color: '$onSurfaceTextCritical',
                  margin: '$quarter 0 0',
                  paddingInlineStart: '$two',
                  typography: '$caption',
                }}
              >
                {formatMessage({ id: 'STR_CONSENT_REQUIRED_ERROR' })}
              </Box>
            )}
          </Box>
          <Checkbox
            id="consent-email-updates"
            name="consent-email-updates"
            checked={optInEmails}
            onChange={(e) => setOptInEmails(e.target.checked)}
            label={emailUpdatesLabel}
            css={{
              '& .GDS-checkbox-label': { padding: 0, borderRadius: '0' },
              '& input': {
                borderRadius: '0',
                borderColor: '$inputBorderDefault',
              },
            }}
          />
        </Box>
      </StackLayout>
    </OnboardingPageWrapper>
  );
};
